import * as React from "react";
import VideoConsultationSettings, {
  CustomVideoConsultationSettings,
  VideoConsultationPlatform,
} from "./settings/VideoConsultationSettings";
import sfetch from "./commons/utils/super-fetch";
import RadioButton from "./commons/components/buttons/RadioButton";
import DailyVideoConsultationPanel from "./settings/DailyVideoConsultationPanel";
import CustomVideoConsultationPanel from "./settings/CustomVideoConsultationPanel";
import SettingsView from "./settings/SettingsView";
import SettingsForm from "./settings/SettingsForm";
import isURL from "validator/es/lib/isURL";
import { AvailableEntitiesSettings } from "./commons/model/EntitySettings";
import { createRoot } from "react-dom/client";

interface SettingsVideoConsultationProps {
  entities: AvailableEntitiesSettings;
  settings: VideoConsultationSettings;
  builtInOptionVisible: boolean;
  customOptionVisible: boolean;
  changeUrl: string;
}

interface SettingsVideoConsultationState {
  persisted: VideoConsultationSettings;
  current: VideoConsultationSettings;
  invalidCustomRoomUrl: boolean;
  invalidCustomHomeUrl: boolean;
  hasChanges: boolean;
}

export default class SettingsVideoConsultation extends React.PureComponent<
  SettingsVideoConsultationProps,
  SettingsVideoConsultationState
> {
  constructor(props: SettingsVideoConsultationProps) {
    super(props);
    const currentPlatform =
      props.customOptionVisible && !props.builtInOptionVisible
        ? VideoConsultationPlatform.CUSTOM
        : props.builtInOptionVisible && !props.customOptionVisible
          ? VideoConsultationPlatform.DAILY
          : props.settings.platform;
    this.state = {
      persisted: props.settings,
      current: {
        ...props.settings,
        platform: currentPlatform,
      } as VideoConsultationSettings,
      hasChanges: props.settings.platform != currentPlatform,
      invalidCustomRoomUrl: false,
      invalidCustomHomeUrl: false,
    } as SettingsVideoConsultationState;
  }

  render() {
    return (
      <SettingsView
        title={i18n["Page.Settings.VideoConsultation.Title"]}
        entities={this.props.entities}
      >
        <SettingsForm
          title={i18n["Page.Settings.Customisation.VideoConsultation.Daily.Disabled.Title"]}
          inactiveSubtitle={
            i18n["Page.Settings.Customisation.VideoConsultation.Daily.Disabled.Subtitle"]
          }
          active={this.state.current.enabled}
          hasChanges={this.state.hasChanges}
          onActivationChange={this.onActivationChange}
          onSave={this.onSave}
          onCancel={this.onCancel}
        >
          {this.renderActive()}
        </SettingsForm>
      </SettingsView>
    );
  }

  private renderActive() {
    return (
      <div className="setting-grid">
        <div className="title">
          {i18n["Page.Settings.Customisation.VideoConsultation.Enabled.Subtitle"]}
        </div>
        <span className="setting-description">
          {this.props.builtInOptionVisible && this.renderDailySettings()}
          {this.props.customOptionVisible && this.renderCustomSettings()}
        </span>
      </div>
    );
  }

  private renderDailySettings() {
    const { platform, daily } = this.state.current;
    const checked = platform == VideoConsultationPlatform.DAILY;
    const visible = checked && daily != null;
    return (
      <div>
        {this.props.builtInOptionVisible && this.props.customOptionVisible && (
          <RadioButton
            name={"video-consultation-platform"}
            value={"daily"}
            checked={checked}
            label={i18n["Page.Settings.Customisation.VideoConsultation.Enabled.Daily.Title"]}
            id={"platform-daily"}
            onChange={this.selectDaily}
          />
        )}
        {visible && <DailyVideoConsultationPanel {...daily} />}
      </div>
    );
  }

  private renderCustomSettings() {
    const { platform, custom } = this.state.current;
    const { invalidCustomRoomUrl, invalidCustomHomeUrl } = this.state;
    const checked = platform == VideoConsultationPlatform.CUSTOM;
    return (
      <div>
        {this.props.builtInOptionVisible && this.props.customOptionVisible && (
          <RadioButton
            name={"video-consultation-platform"}
            value={"custom"}
            checked={checked}
            label={i18n["Page.Settings.Customisation.VideoConsultation.Enabled.Custom.Title"]}
            id={"platform-custom"}
            onChange={this.selectCustom}
          />
        )}
        {checked && (
          <>
            <div className="message info">
              {i18n["Page.Settings.Customisation.VideoConsultation.Enabled.Custom.Info.Message"]}
            </div>
            <CustomVideoConsultationPanel
              settings={custom}
              onChange={this.onCustomVideoConsultationPanelChange}
              invalidHomeUrl={invalidCustomHomeUrl}
              invalidRoomUrl={invalidCustomRoomUrl}
            />
          </>
        )}
      </div>
    );
  }

  private onCustomVideoConsultationPanelChange = (settings: CustomVideoConsultationSettings) => {
    this.setState({
      current: {
        ...this.state.current,
        custom: settings,
      } as VideoConsultationSettings,
      hasChanges: true,
      invalidCustomRoomUrl: false,
      invalidCustomHomeUrl: false,
    });
  };

  private onActivationChange = (active: boolean) => {
    this.setState(
      {
        current: {
          ...this.state.current,
          enabled: active,
        } as VideoConsultationSettings,
        hasChanges: true,
      },
      () => {
        if (!active) {
          return this.send();
        }
      }
    );
  };

  private onCancel = () => {
    this.setState({
      current: this.state.persisted,
      hasChanges: false,
    });
  };

  private onSave = () => {
    const invalidState = this.validate();
    if (invalidState) {
      this.setState(invalidState);
      return Promise.reject();
    } else {
      return this.send();
    }
  };

  private send(): Promise<any> {
    return sfetch(this.props.changeUrl, {
      method: "POST",
      body: JSON.stringify(this.getCurrentSettings()),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((newSettings: VideoConsultationSettings) => {
        this.updateSettings(newSettings);
      });
  }

  private updateSettings(newSettings: VideoConsultationSettings) {
    this.setState({
      persisted: newSettings,
      current: newSettings,
      hasChanges: false,
    });
  }

  private validate(): SettingsVideoConsultationState {
    const custom = this.state.current.custom;
    let validated = true;
    let invalidState: any = {};

    // LongTermFix 2020-06-09 mligocki: need to be rewrite to create no-form components with validation
    if (
      this.state.current.enabled &&
      this.state.current.platform == VideoConsultationPlatform.CUSTOM
    ) {
      if (!custom || !this.isUrl(custom.roomUrl)) {
        validated = false;
        invalidState.invalidCustomRoomUrl = true;
      }
      if (!custom || (custom.homeUrl && !this.isUrl(custom.homeUrl))) {
        validated = false;
        invalidState.invalidCustomHomeUrl = true;
      }
    }

    if (validated) {
      return null;
    } else {
      return invalidState;
    }
  }

  private getCurrentSettings(): VideoConsultationSettings {
    const current = this.state.current;
    if (current.enabled && current.platform == VideoConsultationPlatform.CUSTOM) {
      return current;
    } else {
      return {
        ...current,
        custom: null,
      } as VideoConsultationSettings;
    }
  }

  private isUrl(str: string): boolean {
    return (
      str &&
      str.trim().length > 0 &&
      isURL(str, {
        protocols: ["http", "https"],
        require_protocol: true,
      })
    );
  }

  private selectDaily = () => {
    this.setState({
      current: {
        ...this.state.current,
        platform: VideoConsultationPlatform.DAILY,
      } as VideoConsultationSettings,
      hasChanges: true,
    });
  };

  private selectCustom = () => {
    this.setState({
      current: {
        ...this.state.current,
        platform: VideoConsultationPlatform.CUSTOM,
      } as VideoConsultationSettings,
      hasChanges: true,
    });
  };

  static loadComponent(elementId: string, props: SettingsVideoConsultationProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<SettingsVideoConsultation {...props} />);
  }
}
